import { clientDebugLog } from './clientDebugLog';
import { getKeycloakConfig } from './keycloakConfig';
import { getKeycloak, resetKeycloak } from './keycloak';
import { getKeycloakDefaultScope } from './keycloakOptions';
import { persistKeycloakTokens } from './keycloakTokenRefresh';
import { getRefreshTokenDebugMeta, savePersistedKeycloakSession } from './keycloakTokenStorage';
import { initKeycloak, type InitKeycloakResult } from './keycloakInit';

export type OtpSendResponse = {
	challenge_id: string;
	channel: string;
	destination: string;
	expires_in: number;
	resend_after: number;
};

export type OtpApiError = {
	error: string;
	error_description?: string;
	retry_after?: number;
};

export class OtpApiRequestError extends Error {
	readonly status: number;
	readonly payload: OtpApiError;

	constructor(status: number, payload: OtpApiError) {
		super(
			payload.error_description ??
				payload.error ??
				`OTP API error ${status}`,
		);
		this.name = 'OtpApiRequestError';
		this.status = status;
		this.payload = payload;
	}
}

type TokenResponse = {
	access_token: string;
	refresh_token?: string;
	id_token?: string;
	expires_in?: number;
	refresh_expires_in?: number;
	token_type?: string;
	scope?: string;
};

function otpApiBaseUrl(): string {
	const { url, realm } = getKeycloakConfig();
	return `${url.replace(/\/$/, '')}/realms/${realm}/apexx-otp`;
}

/** Masks phone/email identifiers before remote logging. */
export function maskOtpIdentifier(identifier: string): string {
	const trimmed = identifier.trim();
	if (!trimmed) {
		return '';
	}

	if (trimmed.includes('@')) {
		const [local, domain] = trimmed.split('@');
		if (!domain) {
			return '***';
		}
		const visible = local.slice(0, 1);
		return `${visible}***@${domain}`;
	}

	const digits = trimmed.replace(/\D/g, '');
	if (digits.length <= 4) {
		return '***';
	}

	return `***${digits.slice(-4)}`;
}

function logOtpApiFailure(
	operation: 'send' | 'verify',
	error: unknown,
	context: Record<string, unknown>,
): void {
	if (error instanceof OtpApiRequestError) {
		clientDebugLog('warn', `[auth:otp] ${operation} failed`, {
			...context,
			status: error.status,
			error: error.payload.error,
			errorDescription: error.payload.error_description,
			retryAfter: error.payload.retry_after,
		});
		return;
	}

	clientDebugLog('warn', `[auth:otp] ${operation} failed`, {
		...context,
		message: error instanceof Error ? error.message : String(error),
	});
}

async function postJson<T>(path: string, body: Record<string, string>): Promise<T> {
	const response = await fetch(`${otpApiBaseUrl()}${path}`, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(body),
	});

	const text = await response.text();
	let json: unknown = {};
	if (text) {
		try {
			json = JSON.parse(text) as unknown;
		} catch {
			json = { error: 'invalid_response', error_description: text };
		}
	}

	if (!response.ok) {
		const payload = (json ?? {}) as OtpApiError;
		throw new OtpApiRequestError(response.status, {
			error: payload.error ?? 'request_failed',
			error_description: payload.error_description,
			retry_after: payload.retry_after,
		});
	}

	return json as T;
}

/** Sends an OTP to the given phone/email via Keycloak `/apexx-otp/send`. */
export async function sendOtpApi(options: {
	identifier: string;
	clientId?: string;
	challengeId?: string;
}): Promise<OtpSendResponse> {
	const clientId = options.clientId ?? getKeycloakConfig().clientId;
	const body: Record<string, string> = { client_id: clientId };
	const isResend = Boolean(options.challengeId);
	if (options.challengeId) {
		body.challenge_id = options.challengeId;
	} else {
		body.identifier = options.identifier;
	}

	clientDebugLog('info', '[auth:otp] send requested', {
		clientId,
		isResend,
		identifier: isResend ? undefined : maskOtpIdentifier(options.identifier),
	});

	try {
		const result = await postJson<OtpSendResponse>('/send', body);
		clientDebugLog('info', '[auth:otp] send succeeded', {
			clientId,
			isResend,
			channel: result.channel,
			expiresIn: result.expires_in,
			resendAfter: result.resend_after,
		});
		return result;
	} catch (error) {
		logOtpApiFailure('send', error, {
			clientId,
			isResend,
			identifier: isResend ? undefined : maskOtpIdentifier(options.identifier),
		});
		throw error;
	}
}

/**
 * Verifies OTP, persists the returned Keycloak session, and initializes keycloak-js.
 */
export async function loginWithOtpApi(options: {
	challengeId: string;
	otp: string;
	clientId?: string;
	scope?: string;
}): Promise<InitKeycloakResult> {
	const clientId = options.clientId ?? getKeycloakConfig().clientId;
	const scope = options.scope ?? getKeycloakDefaultScope();

	clientDebugLog('info', '[auth:otp] verify requested', {
		clientId,
		scope,
		challengeIdPresent: Boolean(options.challengeId),
	});

	try {
		const tokens = await postJson<TokenResponse>('/verify', {
			challenge_id: options.challengeId,
			otp: options.otp,
			client_id: clientId,
			scope,
		});

		if (!tokens.access_token || !tokens.refresh_token) {
			clientDebugLog('warn', '[auth:otp] verify returned incomplete tokens', {
				clientId,
				hasAccessToken: Boolean(tokens.access_token),
				hasRefreshToken: Boolean(tokens.refresh_token),
			});
			return { authenticated: false };
		}

		savePersistedKeycloakSession({
			token: tokens.access_token,
			refreshToken: tokens.refresh_token,
			idToken: tokens.id_token,
			savedAt: Date.now(),
		});

		const keycloak = getKeycloak();
		if (keycloak.didInitialize) {
			resetKeycloak();
		}

		const result = await initKeycloak();
		if (result.authenticated && getKeycloak().token) {
			persistKeycloakTokens(getKeycloak());
		}

		clientDebugLog('info', '[auth:otp] verify completed', {
			clientId,
			authenticated: result.authenticated,
			oauthError: result.oauthError?.error,
			oauthErrorDescription: result.oauthError?.error_description,
			...getRefreshTokenDebugMeta(tokens.refresh_token),
		});

		return result;
	} catch (error) {
		logOtpApiFailure('verify', error, { clientId, scope });
		throw error;
	}
}
